import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { getServiceRequestPayload } from "../selector";
import { getCallIdFromCommonBenefits } from "../selector/CommonBenefitSelectors";
import {
  JOURNEY_STEP_TYPES,
  getScreenNameForPath,
  emitJourneyEvent,
  getJourneyBufferSnapshot,
  getJourneyIdentifiers,
  setJourneyIdentifiers,
} from "../utils/journeyMonitoring";

interface JourneyMonitoringContextValue {
  getBufferSnapshot: () => unknown[];
  sessionId?: string;
  callId?: string;
  clientJourneyId?: string;
  emitJourneyEvent?: typeof emitJourneyEvent;
}

/**
 * Provides descendants (e.g. `ErrorBoundary`) read access to the current
 * journey-monitoring buffer/identifiers without introducing module-level
 * mutable state. Default value is a safe no-op shape used whenever a
 * consumer renders outside of `withJourneyMonitoring`.
 */
export const JourneyMonitoringContext = React.createContext<JourneyMonitoringContextValue>({
  getBufferSnapshot: () => [],
  sessionId: undefined,
  callId: undefined,
  clientJourneyId: undefined,
});

/**
 * Higher-order component providing end-to-end user-journey monitoring
 * (spec.md 006-datadog-e2e-monitoring). Always active — journey capture is
 * applied globally from initial rollout, not gated behind a feature flag
 * (FR-011, FR-012).
 *
 * Route views and session/call identification milestones are emitted here.
 * Business-logic milestones (e.g. benefits searched/found, rescue
 * submitted/cancelled) are emitted directly from sagas/components via
 * `utils/journeyMonitoring`, which shares the same buffer/clientJourneyId as
 * this HOC so all events correlate together.
 */
export const withJourneyMonitoring = <P extends object>(
  WrappedComponent: React.ComponentType<P>
): React.ComponentType<P> => {
  const WithJourneyMonitoring = (props: P) => {
    const { sessionId = "" } = useSelector(getServiceRequestPayload) || {};
    const callId = useSelector(getCallIdFromCommonBenefits);
    const location = useLocation();

    const prevIdentifiersRef = React.useRef<{ sessionId?: string; callId?: string }>({
      sessionId: undefined,
      callId: undefined,
    });

    useEffect(() => {
      setJourneyIdentifiers({ sessionId, callId });
      const prev = prevIdentifiersRef.current;
      if (sessionId && sessionId !== prev.sessionId) {
        // A new session has been established for this browser tab/visit.
        emitJourneyEvent(JOURNEY_STEP_TYPES.USER_MILESTONE, "session.established", {
          milestoneName: "session.established",
        });
      }

      if (callId && callId !== prev.callId) {
        // The active rescue call has been identified (callId assigned/changed).
        emitJourneyEvent(JOURNEY_STEP_TYPES.USER_MILESTONE, "call.identified", {
          milestoneName: "call.identified",
        });
      }
      prevIdentifiersRef.current = { sessionId, callId };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId, callId]);

    // Emits a `route.view` journey event on every route transition.
    // screenName is mapped from the URL's trailing path segment to a
    // readable, partner/usertype-agnostic name (e.g. "payment",
    // "memberlookup") via routeScreenNames.ts, so the action name is
    // consistent and searchable across all partners/usertypes sharing a
    // route. The raw path is preserved in metadata for debugging unmapped
    // routes.
    const prevPathnameRef = React.useRef<string | undefined>(undefined);
    useEffect(() => {
      const pathname = location && location.pathname;
      if (!pathname || pathname === prevPathnameRef.current) {
        return;
      }
      prevPathnameRef.current = pathname;
      const screenName = getScreenNameForPath(pathname, location && location.search);

      // Route transition detected; logs the mapped screen name for this URL.
      emitJourneyEvent(JOURNEY_STEP_TYPES.ROUTE_VIEW, `route.${screenName}`, {
        screenName,
        path: pathname.replace(/^\/+/, ""),
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location && location.pathname, location && location.search]);

    const contextValue: JourneyMonitoringContextValue = {
      getBufferSnapshot: getJourneyBufferSnapshot,
      sessionId,
      callId,
      clientJourneyId: getJourneyIdentifiers().clientJourneyId,
      emitJourneyEvent,
    };

    return (
      <JourneyMonitoringContext.Provider value={contextValue}>
        <WrappedComponent {...props} />
      </JourneyMonitoringContext.Provider>
    );
  };

  WithJourneyMonitoring.displayName = `withJourneyMonitoring(${
    WrappedComponent.displayName || WrappedComponent.name || "Component"
  })`;

  return WithJourneyMonitoring;
};
